/**
 * The words for where a sign-in in Prest0n's browser stands (slice D of the browser sign-in program,
 * 2026-10-02; D9 `module:browserSignInPhaseCopy`, class `state`).
 *
 * One table for every surface that shows the phase (browserSignInPhase.ts): the chat's sign-in card today,
 * and the Integrations page, onboarding and the run card's "sign in to <app>" when they move onto the same
 * state. A button's words and, beside it, the one line that says where the sign-in stands. The chat card shows
 * the button's words, and its status line (drawn from the record) takes the line whenever the pill is repainted
 * (browserSignInCards.ts paintStatusLine). State words about the person's own sign-in, never a sentence in
 * Prest0n's voice; "Prest0n" keeps its capital P.
 *
 * `{app}` is the app's name as the card names it.
 */
import type { BrowserSignInPhase } from './browserSignInPhase';

export type BrowserSignInLang = 'en' | 'es';

interface PhaseWords {
  /** The button's words. */
  button: string;
  /** The line beside it ('' = none). */
  status: string;
}

export const BROWSER_SIGNIN_PHASE_COPY: Record<BrowserSignInLang, Record<BrowserSignInPhase, PhaseWords>> = {
  en: {
    'not-started': { button: 'sign in to {app}', status: '' },
    opening: { button: 'opening…', status: "starting Prest0n's secure browser…" },
    open: { button: 'continue in the window', status: 'sign-in window open' },
    'not-finished': { button: 'continue signing in', status: 'sign-in not finished' },
    'signed-in': { button: 'signed in ✓', status: 'signed in' },
    'signed-out': { button: 'sign in again', status: 'signed out — sign in again' },
  },
  es: {
    'not-started': { button: 'iniciar sesión en {app}', status: '' },
    opening: { button: 'abriendo…', status: 'iniciando el navegador seguro de Prest0n…' },
    open: { button: 'continuar en la ventana', status: 'ventana de inicio de sesión abierta' },
    'not-finished': { button: 'continuar con el inicio de sesión', status: 'inicio de sesión sin terminar' },
    'signed-in': { button: 'sesión iniciada ✓', status: 'sesión iniciada' },
    'signed-out': { button: 'volver a iniciar sesión', status: 'sesión cerrada — vuelve a iniciar sesión' },
  },
};

/** The button's words for a phase, in the card's language. */
export function browserSignInButtonWords(phase: BrowserSignInPhase, lang: BrowserSignInLang, app: string): string {
  const table = BROWSER_SIGNIN_PHASE_COPY[lang === 'es' ? 'es' : 'en'];
  return table[phase].button.replace('{app}', String(app || '').slice(0, 60));
}

/** The line beside the button for a phase ('' when the phase has none). */
export function browserSignInStatusWords(phase: BrowserSignInPhase, lang: BrowserSignInLang): string {
  return BROWSER_SIGNIN_PHASE_COPY[lang === 'es' ? 'es' : 'en'][phase].status;
}

/** 2026-10-31: the signed-in line naming the account ("signed in as …"), the words a card reloaded from its record
 *  already says (connectTileRehydrate.ts). The plain signed-in line when the account is not known. */
export const BROWSER_SIGNIN_SIGNED_IN_AS: Record<BrowserSignInLang, string> = {
  en: 'signed in as {who}',
  es: 'sesión iniciada como {who}',
};

export function browserSignInSignedInAsWords(who: string | undefined, lang: BrowserSignInLang): string {
  const name = String(who || '').trim().slice(0, 120);
  if (!name) return browserSignInStatusWords('signed-in', lang);
  return BROWSER_SIGNIN_SIGNED_IN_AS[lang === 'es' ? 'es' : 'en'].replace('{who}', () => name);
}
